/* eslint-disable sonarjs/no-duplicate-string */
import type { PayloadAction } from '@reduxjs/toolkit';
import { logger } from 'lib/logger';
import { RemoveAlbumApiCall } from 'reactApp/api/albums/RemoveAlbumApiCall';
import { RemoveAlbumItemsAPICall } from 'reactApp/api/albums/RemoveAlbumItemsApiCall';
import { IS_PUBLIC_ALBUM } from 'reactApp/appHelpers/configHelpers';
import { renderRemoveDialog } from 'reactApp/components/RemoveDialog/RemoveDialog.helpers';
import { EAlbumAction, sendAlbumAnalytics, sendAlbumItemsAnalytics } from 'reactApp/modules/albums/albums.analytics';
import { getCurrentAlbum } from 'reactApp/modules/albums/albums.selector';
import { isAlbum } from 'reactApp/modules/albums/albums.types';
import { getAllDocumentsCategory } from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { AllDocumentsCategoryType } from 'reactApp/modules/allDocuments/allDocuments.types';
import { handleRemoveAndShowDiscount } from 'reactApp/modules/discountOnRemove/discountOnRemove.saga';
import { removeIncomingPublicRequest } from 'reactApp/modules/incomingPublic/incomingPublic.module';
import {
    type silentlyDeleteFamilyFolderOnFailedCreation,
    modifyingStop,
    removeFileSuccess,
} from 'reactApp/modules/modifying/modifying.actions';
import { removeErrors } from 'reactApp/modules/modifying/modifying.constants';
import { getSnackbarText } from 'reactApp/modules/modifying/modifying.helpers';
import { type IRemoveItems, type PublishItem, EModifyReason, ESnackbarType } from 'reactApp/modules/modifying/modifying.types';
import { closePopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { getPublicItemId } from 'reactApp/modules/public/public.helpers';
import { getPublicRootWeblink, isOwnPublic } from 'reactApp/modules/public/public.selectors';
import { changeHistory } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { isMountedFolder } from 'reactApp/modules/storage/folder.helpers';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { deleteFilesFromBinRequest } from 'reactApp/modules/trashbin/trashbin.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { requestRemoveFromVigboAlbum, requestRemoveVigboAlbum } from 'reactApp/modules/vigbo/vigbo.helpers';
import { getCurrentVigboAlbum } from 'reactApp/modules/vigbo/vigbo.selectors';
import { noopVoid } from 'reactApp/utils/helpers';
import { captureException } from 'reactApp/utils/tracer';
import { channel } from 'redux-saga';
import { call, put, select, take } from 'redux-saga/effects';

import { handleUmountFolder } from './mount.saga';
import { unshareAlbumAPICall } from './publish.saga';
import { handleRemoveItemsRequest, removeRequest } from './removeItems.saga';

const type = ESnackbarType.REMOVE;

const removeAlbumApiCall = ({ id }) => new RemoveAlbumApiCall().makeRequest({ album_id: id });
const removeAlbumItemsApiCall = ({ removeFromCloud = false, itemsId, albumId }) =>
    new RemoveAlbumItemsAPICall().makeRequest({
        remove_from_cloud: removeFromCloud,
        element_ids: itemsId,
        album_id: albumId,
    });

export function* handleSilentDeleteFamilyFolder(action: ReturnType<typeof silentlyDeleteFamilyFolderOnFailedCreation>) {
    const { item } = action.payload;
    const storage = EStorageType.home; // На данный момент семейные папки добавляются в Home

    try {
        yield removeRequest(storage, item);
        yield put(removeFileSuccess({ ids: [item.id] }));
        yield put(deleteFilesFromBinRequest({ selected: [item.id] }));
    } catch (error: any) {}
}

function* handleRemoveAlbumRequest(item) {
    // Перед удалением нужно сделать анпаблиш
    if (item.weblink) {
        yield call(unshareAlbumAPICall, { id: item.id });
    }

    yield removeAlbumApiCall({ id: item.id });
    const currentAlbum = yield select(getCurrentAlbum);

    yield put(
        showSnackbarAction({
            closable: true,
            id: 'delete-items-success',
            type: SnackbarTypes.success,
            text: getSnackbarText({ items: [item], albumsCount: 1, type }),
        })
    );

    yield closePopupHelper(popupNames.REMOVE_DIALOG);

    sendAlbumAnalytics({ action: EAlbumAction.DELETE });

    yield put(removeFileSuccess({ ids: [item.id] }));

    if (item.id === currentAlbum?.id) {
        yield put(changeHistory('/albums'));
    }
}

function* handleRemoveVigboAlbumRequest(item) {
    // Перед удалением нужно сделать анпаблиш
    if (item.weblink) {
        yield call(unshareAlbumAPICall, { id: item.id });
    }

    yield requestRemoveVigboAlbum({ album_id: item.id });
    const currentAlbum = yield select(getCurrentVigboAlbum);

    yield put(
        showSnackbarAction({
            closable: true,
            id: 'delete-items-success',
            type: SnackbarTypes.success,
            text: getSnackbarText({ items: [item], vigboAlbumsCount: 1, type }),
        })
    );

    yield closePopupHelper(popupNames.REMOVE_DIALOG);

    sendAlbumAnalytics({ action: EAlbumAction.DELETE });

    yield put(removeFileSuccess({ ids: [item.id] }));

    if (item.id === currentAlbum?.id) {
        yield put(changeHistory('/vigbo'));
    }
}

function* handleRemoveAlbumItemsRequest(items) {
    // После появления совместной загрузки в альбомы,
    // уточнить у менеджеров и предусмотреть,
    // что удалять можно только свои файлы

    const storage = yield select(getCurrentStorage);
    const isAlbumPage = storage === EStorageType.albums;

    try {
        if (items.length === 1 && isAlbum(items[0])) {
            yield handleRemoveAlbumRequest(items[0]);
            return;
        }

        const currentAlbum = yield select(isAlbumPage ? getCurrentAlbum : getPublicRootWeblink);
        const albumId = isAlbumPage ? currentAlbum.id : currentAlbum.albumId;
        const itemsId = isAlbumPage ? items.map((item) => item.id) : items.map((item) => getPublicItemId(item.id));

        const { data } = yield removeAlbumItemsApiCall({ itemsId, albumId });
        const failedItems = data?.failed?.map((item) => item.id) || [];
        const deletedItems = failedItems.length ? items.filter((item) => !failedItems.includes(item.id)) : items;
        const filesCount = deletedItems.length;

        if (!filesCount) {
            yield put(
                showSnackbarAction({
                    id: 'remove-items-error',
                    type: SnackbarTypes.failure,
                    text: removeErrors.getMessage('unknown'),
                    closable: true,
                })
            );

            return;
        }

        yield put(
            showSnackbarAction({
                closable: true,
                id: 'delete-items-success',
                type: SnackbarTypes.success,
                text: getSnackbarText({ items: deletedItems, filesCount, type }),
            })
        );

        sendAlbumItemsAnalytics({ action: EAlbumAction.DELETE_MEDIA, items });

        yield put(removeFileSuccess({ ids: deletedItems.map((item) => item?.id).filter(Boolean) }));
    } catch (error) {
        yield put(
            showSnackbarAction({
                id: 'remove-items-error',
                type: SnackbarTypes.failure,
                text: removeErrors.getMessage(error),
                closable: true,
            })
        );
    }

    yield closePopupHelper(popupNames.REMOVE_DIALOG);
}

function* handleRemoveVigboAlbumItemsRequest(items) {
    const storage = yield select(getCurrentStorage);
    const isVigbo = storage === EStorageType.vigbo;

    try {
        if (items.length === 1 && isAlbum(items[0])) {
            yield handleRemoveVigboAlbumRequest(items[0]);
            return;
        }

        const currentAlbum = yield select(isVigbo ? getCurrentVigboAlbum : getPublicRootWeblink);
        const albumId = isVigbo ? currentAlbum.id : currentAlbum.albumId;
        const itemsId = isVigbo ? items.map((item) => item.id) : items.map((item) => getPublicItemId(item.id));

        const { data } = yield requestRemoveFromVigboAlbum({ element_ids: itemsId, album_id: albumId });

        const failedItems = data?.failed?.map((item) => item.id) || [];
        const deletedItems = failedItems.length ? items.filter((item) => !failedItems.includes(item.id)) : items;
        const filesCount = deletedItems.length;

        if (!filesCount) {
            yield put(
                showSnackbarAction({
                    id: 'remove-items-error',
                    type: SnackbarTypes.failure,
                    text: removeErrors.getMessage('unknown'),
                    closable: true,
                })
            );

            return;
        }

        yield put(
            showSnackbarAction({
                closable: true,
                id: 'delete-items-success',
                type: SnackbarTypes.success,
                text: getSnackbarText({ items: deletedItems, filesCount, type }),
            })
        );

        sendAlbumItemsAnalytics({ action: EAlbumAction.DELETE_MEDIA, items });

        yield put(removeFileSuccess({ ids: deletedItems.map((item) => item?.id).filter(Boolean) }));
    } catch (error) {
        yield put(
            showSnackbarAction({
                id: 'remove-items-error',
                type: SnackbarTypes.failure,
                text: removeErrors.getMessage(error),
                closable: true,
            })
        );
    }

    yield closePopupHelper(popupNames.REMOVE_DIALOG);
}

function* askForDeleteFiles({ items, hasAlienFiles = false, onSuccess = noopVoid }) {
    const deleteChannel = channel();
    const storage = yield select(getCurrentStorage);

    renderRemoveDialog({
        items,
        hasAlienFiles,
        onClose: () => deleteChannel.close(),
        onRemove: () => {
            onSuccess();
            deleteChannel.put(true);
        },
    });

    yield take(deleteChannel);
    yield deleteChannel.close();

    if (IS_PUBLIC_ALBUM || storage === EStorageType.albums) {
        yield handleRemoveAlbumItemsRequest(items);

        return;
    }

    if (storage === EStorageType.vigbo) {
        yield handleRemoveVigboAlbumItemsRequest(items);

        return;
    }

    yield handleRemoveItemsRequest(items);
}

function* processRemovePublicItems({ items }) {
    const isOwn = yield select(isOwnPublic);
    const email = yield select(UserSelectors.getEmail);

    // Если файлы пытается удалить не автор паблика, нужно отфильтровать чужие файлы
    const filteredItems = isOwn ? items : items.filter((item) => item.author === email);
    const hasAlienFiles = filteredItems.length !== items.length;

    // Оставляем только файлы, потому что удалять папки можно только внутри облака
    const files = filteredItems.filter((item) => !item.isFolder);
    const hasFolder = filteredItems.length !== files.length;

    if (hasFolder) {
        yield put(
            showSnackbarAction({
                id: 'folder-remove-error',
                text: removeErrors.getMessage('external_cloud'),
                type: SnackbarTypes.failure,
                closable: true,
            })
        );
    }

    if (!files.length && !hasAlienFiles) {
        return;
    }

    yield askForDeleteFiles({ items: files, hasAlienFiles });
}

export function* handleRemoveItems(action: PayloadAction<IRemoveItems>) {
    const storage = yield select(getCurrentStorage);
    const { isSharedIncoming, isPublic, isIncomingPublic, isAllDocuments } = getStorage(storage);
    const allDocumentsCategory = yield select(getAllDocumentsCategory);

    const { items, withRemoveDialog = true, onSuccess = noopVoid } = action.payload;

    if (!items?.length) {
        return;
    }

    // tempexp_19300-start
    const cancelRemove = yield handleRemoveAndShowDiscount();
    if (cancelRemove) {
        return;
    }
    // tempexp_19300-end

    if (!withRemoveDialog) {
        yield handleRemoveItemsRequest(items);

        return;
    }

    const item = items[0];

    if (isSharedIncoming || (items.length === 1 && item && isMountedFolder(item))) {
        yield handleUmountFolder({ item: items[0] as PublishItem });
        return;
    }

    if (isIncomingPublic || (isAllDocuments && allDocumentsCategory === AllDocumentsCategoryType.sharedWithYou)) {
        yield put(removeIncomingPublicRequest({ weblink_ids: items.map((item) => item.id) }));
        return;
    }

    try {
        yield call(isPublic ? processRemovePublicItems : askForDeleteFiles, { items, onSuccess });
    } catch (error) {
        logger.error(error);
        captureException(error);
        yield put(modifyingStop({ storage, reason: EModifyReason.remove }));
    }
}
